import React, { useMemo, useState, useEffect } from "react";
import ReactECharts from "echarts-for-react";
import { useTranslation } from "react-i18next";
import NumberUtil from "../../utility/NumberUtil";
import { ToggleButton } from "primereact/togglebutton";
import PrimeDialog from "../../components/dialog/PrimeDialog";
import { Button } from "primereact/button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export default function CusChart({ title, dataList, charType = "pie", globalViewMode = null }) {
  const defaultMaxCategories = 5;
  const { t } = useTranslation();
  const [isChecked, setIsChecked] = useState(true);
  const [popup, setPopup] = useState(false);
  const [popupIsChecked, setPopupIsChecked] = useState(true);
  const [hasIndividualOverride, setHasIndividualOverride] = useState(false);

  // 當有全域控制且沒有個別覆蓋時，使用全域模式；否則使用個別狀態
  const currentViewMode = globalViewMode !== null && !hasIndividualOverride ? globalViewMode : isChecked;

  // 當全域模式改變時，同步更新個別狀態並重置覆蓋狀態
  useEffect(() => {
    if (globalViewMode !== null) {
      setIsChecked(globalViewMode);
      setHasIndividualOverride(false);
    }
  }, [globalViewMode]);
  // 圖表顏色配置
  const COLORS = ["#5770C5", "#92CC76", "#FAC859", "#ED6767", "#73C0DD", "#bda29a"];

  // 長條圖
  const getBarOptions = (data, maxCategories) => {
    const processed = processData(data, maxCategories);

    return {
      title: {
        text: `${t("row_count")}: ${data && data.length > 0 ? data[0]?.totalValue ?? 0 : 0}`,
        left: "center",
        top: "top",
        textStyle: { fontSize: 16 },
      },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
      },
      xAxis: {
        type: "category",
        data: processed.map((item) => item.labelName),
        axisLabel:
          processed.length > defaultMaxCategories + 1
            ? {
                width: 80,
                overflow: "truncate",
                rotate: 45, // 標籤旋轉，避免擠在一起
                interval: 0, // 每個都顯示
              }
            : {
                interval: 0,
                overflow: "truncate",
                width: 80,
              },
      },
      yAxis: {
        type: "value",
        axisLabel: {
          formatter: (value) => Math.round(value),
        },
        minInterval: 1,
      },
      series: [
        {
          type: "bar",
          data: processed.map((item, index) => ({
            value: Number(item.value),
            itemStyle: {
              color: COLORS[index % COLORS.length], // 循環使用顏色
            },
          })),
          label: {
            show: true,
            position: "top",
            formatter: function(params) {
              return NumberUtil.formatWithCommas(params.value);
            },
            textStyle: {
              fontSize: 12,
              fontWeight: "bold",
            },
          },
        },
      ],
      ...(processed.length > defaultMaxCategories + 1 && {
        dataZoom: [{ type: "slider", start: 0, end: 50 }, { type: "inside" }],
      }),
    };
  };

  // 圓餅圖
  const getPieOptions = (data) => {
    return {
      title: {
        text: `${t("row_count")}: ${data[0]?.totalValue ?? 0}`,
        left: "center",
        top: "top",
        textStyle: { fontSize: 16 },
      },
      tooltip: {
        trigger: "item",
        formatter: "{b}: {c} ({d}%)",
      },
      legend: {
        orient: "horizontal",
        left: "center",
        bottom: "2%",
        itemGap: 10,
        textStyle: {
          fontSize: 12,
        },
        formatter: function(name) {
          // 找到對應的資料項目
          const item = data.find((d) => (d.label === "D" ? t("deactivate") : d.labelName) === name);
          if (item) {
            return `${name}: ${NumberUtil.formatWithCommas(item.value)}`;
          }
          return name;
        },
      },
      grid: {
        left: "5%",
        right: "5%",
        top: "5%",
        bottom: "20%", // 為底部圖例留出更多空間
      },
      series: [
        {
          type: "pie",
          radius: ["35%", "60%"], // 調整圓餅圖大小
          center: ["50%", "45%"], // 圓心完全居中
          data: data.map((item, index) => ({
            value: Number(item.value),
            name: item.label === "D" ? t("deactivate") : item.labelName,
            itemStyle: { color: COLORS[index % COLORS.length] },
          })),
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: "rgba(0, 0, 0, 0.5)",
            },
          },
        },
      ],
    };
  };

  const processData = (data, maxCategories = defaultMaxCategories) => {
    if (data.length <= maxCategories) return data;

    // 依數值排序，取前 N 個
    const sorted = [...data].sort((a, b) => Number(b.value) - Number(a.value));
    const top = sorted.slice(0, maxCategories);

    // 其餘的合併
    const others = sorted.slice(maxCategories);
    const otherValue = others.reduce((sum, item) => sum + Number(item.value), 0);

    return [...top, { labelName: t("other"), value: Number(otherValue) }];
  };

  const getOptions = (data, type = "pie", maxCategories) => {
    switch (type) {
      case "bar":
        return getBarOptions(data, maxCategories);
      default:
        return getPieOptions(data);
    }
  };

  // 取得統計表資料
  const gasForm = (data) => {
    return (
      <div className="dashboard-gas-table-wrapper">
        {data?.map((item, index) => (
          <div key={index} className="dashboard-gas-table">
            <div className="dashboard-gas-table-header">{item.label === "D" ? t("deactivate") : item?.labelName}</div>
            <div className="dashboard-gas-table-col font-bold">{t("quantity")}</div>
            <div className="dashboard-gas-table-col text-right">{NumberUtil.formatWithCommas(item?.value)}</div>
            <div className="dashboard-gas-table-col font-bold">{t("percentage")}</div>
            <div className="dashboard-gas-table-col text-right">
              {" "}
              {item?.percentage ? `${NumberUtil.formatWithCommas(item?.percentage)} %` : "-"}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="p-card">
      <div className="p-card-body">
        <div className="p-card-title">
          <span style={{ display: "inline-flex", alignItems: "baseline", gap: "4px" }}>{title}</span>
          {/* 按鈕移回右上角 */}
          <div style={{ display: "flex", gap: "4px" }}>
            {/* popup 顯示更多 */}
            {dataList.length > 5 && (
              <Button
                tooltip={t("displayMore")}
                tooltipOptions={{ position: "top" }}
                rounded
                text
                severity="secondary"
                className="p-button-icon-only"
                onClick={(e) => {
                  setPopup(true);
                }}
              >
                <FontAwesomeIcon icon="fa-solid fa-expand" />
              </Button>
            )}
            {/* 切換表 / 圖 - 個別切換按鈕 */}
            <Button
              tooltip={isChecked ? t("statisticsTable") : t("SummaryGraph")}
              tooltipOptions={{ position: "top" }}
              rounded
              text
              severity="secondary"
              className="p-button-icon-only"
              icon={isChecked ? "pi pi-table" : "pi pi-chart-pie"}
              onClick={() => {
                setIsChecked(!isChecked);
                setHasIndividualOverride(true);
              }}
            />
          </div>
        </div>
        <div className="dashboard-table-wrapper">
          {currentViewMode ? (
            <ReactECharts
              option={getOptions(dataList, charType)}
              style={{ height: "400px", width: "100%", minHeight: "300px" }}
            />
          ) : (
            <>{dataList && gasForm(dataList)} </>
          )}
        </div>
      </div>

      <PrimeDialog
        visible={popup}
        onClose={() => setPopup(false)}
        isShowConfBtn={false}
        size="lg"
        title={t("displayMore")}
        bodyTemplate={
          <>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
              {/* 左側 title + small */}
              <span style={{ display: "inline-flex", alignItems: "baseline", gap: "4px" }}>{title}</span>

              {/* 右側按鈕群 */}
              <div style={{ display: "inline-flex", gap: "4px" }}>
                {/* 切換表 / 圖 */}
                <Button
                  tooltip={popupIsChecked ? t("statisticsTable") : t("SummaryGraph")}
                  tooltipOptions={{ position: "top" }}
                  rounded
                  text
                  severity="secondary"
                  className="p-button-icon-only"
                  icon={popupIsChecked ? "pi pi-table" : "pi pi-chart-pie"}
                  onClick={() => setPopupIsChecked(!popupIsChecked)}
                />
              </div>
            </div>

            {popupIsChecked ? (
              <ReactECharts
                option={getOptions(dataList, charType, dataList.length)}
                style={{ height: "500px", width: "100%" }}
              />
            ) : (
              <>{dataList && gasForm(dataList)} </>
            )}
          </>
        }
      ></PrimeDialog>
    </div>
  );
}
